import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationType } from '@app/shared/interfaces';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Owner } from '../../models/statements.models';
import { StatementsFacade } from '../../services/statements.facade';

@Component({
  standalone: false,
  selector: 'hospitable-statements-add-edit-owner',
  templateUrl: './statements-add-edit-owner.component.html',
  styleUrls: ['./statements-add-edit-owner.component.css'],
})
export class StatementsAddEditOwnerComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();

  mode$ = new BehaviorSubject<'add' | 'edit'>('add');
  loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  saving$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  deleting$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  owner$: BehaviorSubject<Owner> = new BehaviorSubject(this.statementService.newOwner());

  propertiesState$ = this.statementService.propertiesState$;

  constructor(
    private notificationService: ToastNotificationsService,
    private statementService: StatementsFacade,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Fetch available property details, we'll need these for the property picker
    this.statementService.getProperties();

    this.route.paramMap.pipe(takeUntil(this.destroyed$)).subscribe((params) => {
      if (!params.has('id')) {
        return;
      }
      this.loading$.next(true);
      this.mode$.next('edit');

      const ownerId = Number(params.get('id'));
      this.loadOwnerDetails(ownerId);
    });
  }

  loadOwnerDetails(id: Owner['id']) {
    this.statementService
      .getOwner(id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((owner) => {
        this.owner$.next(owner);
        this.loading$.next(false);
      });
  }

  deleteOwner(id: Owner['id']) {
    this.deleting$.next(true);

    this.statementService.deleteOwner(id).subscribe({
      next: () => {
        this.deleting$.next(false);
        this.router.navigate(['../'], { relativeTo: this.route });
      },
      error: (error) => {
        this.deleting$.next(false);
        this.notificationService.open(
          error?.message ?? 'An error occurred while deleting the owner.',
          'OK',
          NotificationType.Error
        );
      },
    });
  }

  save() {
    this.saving$.next(true);

    const owner = this.owner$.value;
    const saveMethod = this.mode$.value === 'add' ? 'createOwner' : 'updateOwner';

    this.statementService[saveMethod](owner).subscribe({
      next: () => {
        this.saving$.next(false);
        this.router.navigate(['../'], { relativeTo: this.route });
      },
      error: (error) => {
        this.saving$.next(false);
        this.notificationService.open(
          error?.message ?? 'An error occurred while saving the owner.',
          'OK',
          NotificationType.Error
        );
      },
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
